import React, { useEffect, useCallback, useMemo, memo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import Popup from '../../../components/helpers/Popup';
import Button from '../../../components/helpers/Button';
import { Spinner } from '../../../components/helpers/Spinner';
import { handleSendSearchCriteria, handleResetSearchCriteriaHash } from '../../../actions/browse';
import { usePopupContext } from '../../../contexts/PopupContext';

import style from './style.module.css';

/** Share search criteria modal component. */
const ShareLinkPopupContainer = ({
  searchCriteriaHash,
  popup,
  handleSendSearchCriteria,
  handleResetSearchCriteriaHash,
}) => {
  const { closePopup } = usePopupContext();

  useEffect(() => {
    const requestParams = popup.getIn(['props', 'requestParams']).toJS();
    const stateParams = popup.getIn(['props', 'stateParams']).toJS();

    /** Send params to backend on mount modal. */
    handleSendSearchCriteria({ requestParams, stateParams });

    return () => {
      /** Reset search criteria hash on unmount. */
      handleResetSearchCriteriaHash();
    };
  }, []);

  /** Close popup method. */
  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const isFetching = popup.get('isPopupFetching');
  const name = popup.getIn(['props', 'name']);
  const header = useMemo(() => `Sharing browse/${name} criteria`, [name]);

  /** Modal footer. */
  const footer = useMemo(
    () => (
      <Button key="close" onClick={handleClose}>
        Close
      </Button>
    ),
    [],
  );

  /** Create share link. */
  const link = useMemo(
    () => searchCriteriaHash && `${window.location.origin}/browse/${name}?criteria=${searchCriteriaHash}`,
    [searchCriteriaHash, name],
  );

  /** Render modal body method. */
  const renderBody = useMemo(
    () =>
      isFetching ? (
        <Spinner className="loader text-primary" inline />
      ) : (
        <div>
          Your shared link:{' '}
          <a href={link} rel="noopener noreferrer" target="_blank">
            {link}
          </a>
        </div>
      ),
    [isFetching, link],
  );

  return (
    <Popup footer={footer} header={header} id="shareLinkPopup" loading>
      <div className={classNames(style.body, 'container')}>{renderBody}</div>
    </Popup>
  );
};

const mapStateToProps = (state, props) => ({
  ...props,
  searchCriteriaHash: state.browse.get('searchCriteriaHash'),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(
    {
      handleSendSearchCriteria,
      handleResetSearchCriteriaHash,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(ShareLinkPopupContainer));
